// The per-project P&L layout editor. Spec task 9, mode A.
//
// WHAT THIS SCREEN CHANGES AND WHAT IT CANNOT.
// Fifteen expense categories are configured for the whole organisation and
// every one of them renders on every project, while a typical project uses
// about six. This is the control that was missing: a project says which of
// the organisation's lines it shows, in what order, under what word. It does
// not change a single figure. The resolver hands the P&L tab the full
// organisation list for computation no matter what this editor does
// (projectPnlView.ts explains why at length), so a row leaving this screen can
// never take money out of Net Profit, and can never put the tab and the
// company dashboard into disagreement.
//
// THE RULE THAT KEEPS THAT HONEST. Hiding is offered only for a category that
// holds no value row on this project, in any month, from either source. That
// is not a policy this component applies on top of the data: it is the
// resolver's emptyCandidates, computed from the tab's own full fetch. A
// category that holds money cannot be hidden here at all. Removing that money
// is a different act, it is destructive, and it lives in
// PnlClearCategoryDialog with its own confirmation and its own recovery path.
//
// MODE B'S ENTRY POINT, AND WHY IT IS OFF WHILE THERE ARE UNSAVED CHANGES. The
// clear RPC writes pnl_view.hidden itself, server side, in the same transaction
// as the delete. If this editor then saved a draft prepared before that, the
// draft would write the category back out of the hidden list and leave the row
// on screen with no money behind it, which is drift this design goes to some
// trouble to prevent. So Clear is offered only from a clean editor: save or
// cancel first, and the button says so rather than failing later.
//
// ONE WRITE, ON COMMIT. Every write to claims_project_config snapshots the
// whole row into its history table, so this editor holds a draft and saves it
// once when the user presses Save. Nothing is written per drag frame or per
// keystroke. Cancel discards, and because the draft is never applied
// optimistically, the P&L behind this dialog does not move until the database
// has confirmed the write.
//
// PERMISSIONS. The toolbar button that opens this sits inside
// RoleProtectedWrapper, so reaching it at all requires can_view_financials,
// which is the rule Elijah set for the non-destructive path. Organisation role
// is deliberately not consulted anywhere in mode A: in this organisation one
// super_admin has can_view_financials false and one ordinary member has it
// true, so a role check here would be wrong about real people in both
// directions.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertTriangle,
  Check,
  Eye,
  EyeOff,
  GripVertical,
  Loader2,
  Package,
  Pencil,
  RotateCcw,
  Trash2,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { Modal, useToast } from "../ui/ToastModal";
import { PnlArchivedValues } from "./PnlArchivedValues";
import { PnlClearCategoryDialog } from "./PnlClearCategoryDialog";
import { describeClearOutcome, describeRestoreOutcome } from "../../features/pnlAnalytics/clearResultDecode";
import { useClearProjectCategory } from "../../features/pnlAnalytics/useClearProjectCategory";
import { formatCurrency } from "../../lib/calculations";
import { formatMonthLabel } from "../../features/pnlAnalytics/pnlAnalyticsFormat";
import {
  resolveProjectPnlRows,
  valuedCategories,
  withHidden,
  withLabel,
  withOrder,
  type ProjectPnlCategoryLike,
  type ProjectPnlRow,
  type ProjectPnlValueRowLike,
  type ProjectPnlView,
} from "../../features/pnlAnalytics/projectPnlView";
import "./PnlProjectView.css";

/** One value row as the P&L tab already holds it, with the id mode B will need. */
export interface ProjectPnlValueRowWithId extends ProjectPnlValueRowLike {
  id: string;
}

export interface PnlProjectViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  /** The organisation list, in its stored order. Never filtered before it gets here. */
  orgCategories: ProjectPnlCategoryLike[];
  /** Every value row on this project, both sources, every month. */
  valueRows: ProjectPnlValueRowWithId[];
  /** The stored overlay. The draft starts here and this is never mutated. */
  view: ProjectPnlView;
  /** Set when the overlay could not be read, so the editor says so rather than pretending. */
  loadError: string | null;
  saving: boolean;
  /** Resolves false with a message when the database refused the write. */
  onSave: (next: ProjectPnlView) => Promise<{ ok: boolean; message?: string }>;
  /**
   * Organisation admin AND financial access, together.
   *
   * Both halves, because in this organisation neither implies the other: one
   * super_admin has can_view_financials false and one ordinary member has it
   * true. The database enforces the same pair inside both RPCs and in the
   * archive's SELECT policy (20260915000300); this flag only decides whether a
   * control that would be refused is drawn at all.
   */
  canClear: boolean;
  /** Called after a clear or a restore, so the tab refetches its values and its overlay. */
  onValuesChanged: () => void;
}

/** The order the P&L tab renders its expense sections in, with its icons. */
const GROUPS = [
  { name: "Materials" as const, label: "Materials", icon: Package },
  { name: "Other" as const, label: "Other costs", icon: Wrench },
  { name: "Wages" as const, label: "Wages", icon: Users },
];

const MAX_LABEL_LENGTH = 60;

/** What this project holds under one category, summarised for a person. */
interface CategoryFacts {
  rowCount: number;
  monthCount: number;
  total: number;
  firstMonth: string | null;
  lastMonth: string | null;
  sources: string[];
}

function summariseByCategory(rows: readonly ProjectPnlValueRowWithId[]): Map<string, CategoryFacts> {
  const byCategory = new Map<string, { months: Set<string>; sources: Set<string>; total: number; rowCount: number }>();
  for (const row of rows) {
    let entry = byCategory.get(row.category);
    if (!entry) {
      entry = { months: new Set(), sources: new Set(), total: 0, rowCount: 0 };
      byCategory.set(row.category, entry);
    }
    entry.months.add(row.monthDate);
    entry.sources.add(row.source);
    entry.total += row.amount;
    entry.rowCount += 1;
  }

  const facts = new Map<string, CategoryFacts>();
  for (const [category, entry] of byCategory) {
    const months = [...entry.months].sort();
    facts.set(category, {
      rowCount: entry.rowCount,
      monthCount: months.length,
      total: entry.total,
      firstMonth: months[0] ?? null,
      lastMonth: months[months.length - 1] ?? null,
      sources: [...entry.sources].sort(),
    });
  }
  return facts;
}

/** "Sep 2026", or "Feb 2026 to Sep 2026" when the values span more than one month. */
function describeSpan(facts: CategoryFacts): string {
  if (!facts.firstMonth) return "";
  if (facts.monthCount === 1) return formatMonthLabel(facts.firstMonth);
  return `${formatMonthLabel(facts.firstMonth)} to ${formatMonthLabel(facts.lastMonth ?? facts.firstMonth)}`;
}

/**
 * Two overlays are the same when their hidden list, their order and their
 * label map all match.
 *
 * Compared through JSON rather than by joining on a separator: category names
 * carry spaces and ampersands ("Handrails & Stairs", "Misc Projects"), so any
 * separator that can also appear inside a name makes two different lists
 * compare equal, and this value is what decides whether Save is live.
 */
function sameView(a: ProjectPnlView, b: ProjectPnlView): boolean {
  return (
    JSON.stringify(a.hidden) === JSON.stringify(b.hidden) &&
    JSON.stringify(a.order) === JSON.stringify(b.order) &&
    JSON.stringify(Object.entries(a.labels).sort()) === JSON.stringify(Object.entries(b.labels).sort())
  );
}

/**
 * Reorders one group's visible names inside the project's full ordering,
 * leaving every other name exactly where it was.
 *
 * The overlay stores ONE flat order covering every organisation category,
 * including the hidden ones, so that unhiding a category returns it to the
 * place the project put it rather than to the end of the list. A drag,
 * though, happens inside one rendered group. This walks the full order and
 * replaces each member of the dragged group with the next name from its new
 * sequence, so hidden names and other groups keep their positions.
 */
function reorderWithinFullOrder(
  fullOrder: readonly string[],
  groupNames: readonly string[],
  nextGroupNames: readonly string[],
): string[] {
  const inGroup = new Set(groupNames);
  let cursor = 0;
  return fullOrder.map((name) => (inGroup.has(name) ? nextGroupNames[cursor++] ?? name : name));
}

interface SortableRowProps {
  row: ProjectPnlRow;
  facts: CategoryFacts | undefined;
  canHide: boolean;
  /** Null when this row offers no clear: no values, no permission, or unsaved changes. */
  clearBlockedReason: string | null;
  onClear: (() => void) | null;
  editing: boolean;
  onStartEditing: () => void;
  onStopEditing: () => void;
  onLabel: (label: string) => void;
  onHide: () => void;
}

function SortableRow({
  row,
  facts,
  canHide,
  clearBlockedReason,
  onClear,
  editing,
  onStartEditing,
  onStopEditing,
  onLabel,
  onHide,
}: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.category,
  });
  const [draftLabel, setDraftLabel] = useState(row.isRelabelled ? row.label : "");

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
    position: isDragging ? "relative" : undefined,
  };

  function commitLabel() {
    onLabel(draftLabel);
    onStopEditing();
  }

  const className = ["ppv-row", isDragging ? "is-dragging" : "", row.driftVisible ? "is-drift" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <li ref={setNodeRef} style={style} className={className}>
      <button
        type="button"
        className="ppv-handle"
        aria-label={`Reorder ${row.category}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} aria-hidden="true" />
      </button>

      {editing ? (
        <div className="ppv-label-edit">
          <input
            className="ppv-label-input"
            value={draftLabel}
            maxLength={MAX_LABEL_LENGTH}
            autoFocus
            aria-label={`What this project calls ${row.category}`}
            placeholder={row.category}
            onChange={(event) => setDraftLabel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitLabel();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                onStopEditing();
              }
            }}
          />
          <button type="button" className="ppv-icon-btn" onClick={commitLabel} aria-label="Use this name">
            <Check size={16} aria-hidden="true" />
          </button>
          <button type="button" className="ppv-icon-btn" onClick={onStopEditing} aria-label="Keep the current name">
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      ) : (
        <>
          <div className="ppv-names">
            <span className="ppv-name">{row.label}</span>
            {row.isRelabelled && (
              <span className="ppv-name-canonical">
                stored as <code>{row.category}</code>, so every figure and every report still reads it that way
              </span>
            )}
            {row.driftVisible ? (
              <span data-seq-private="" className="ppv-money">
                Hidden, but {formatCurrency(facts?.total ?? 0)} was typed here after it was hidden, so the row is back.
              </span>
            ) : facts ? (
              <span data-seq-private="" className="ppv-money">
                {formatCurrency(facts.total)} across {facts.monthCount}{" "}
                {facts.monthCount === 1 ? "month" : "months"}, {describeSpan(facts)}
              </span>
            ) : (
              <span className="ppv-money is-empty">No values on this project, in any month</span>
            )}
          </div>

          <div className="ppv-actions">
            {row.driftVisible ? (
              <span className="ppv-chip is-drift">
                <AlertTriangle size={11} aria-hidden="true" /> holds values
              </span>
            ) : (
              // Why the hide button beside this is off. A disabled control with
              // the reason only in its title attribute explains itself to a
              // mouse and to nobody else.
              !canHide && <span className="ppv-chip is-held">holds values</span>
            )}
            <button
              type="button"
              className="ppv-icon-btn"
              onClick={onStartEditing}
              aria-label={`Rename ${row.category} on this project`}
              title={`Rename ${row.category} on this project`}
            >
              <Pencil size={15} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="ppv-icon-btn"
              onClick={onHide}
              disabled={!canHide}
              aria-label={`Hide ${row.category} on this project`}
              title={
                canHide
                  ? `Hide ${row.category} on this project`
                  : `${row.category} holds values on this project, so hiding it would leave money on the company dashboard with no row here`
              }
            >
              <EyeOff size={15} aria-hidden="true" />
            </button>
            {/* Destructive last, after the two safe actions, so the
                pointer never passes over it on the way to Rename or Hide. */}
            {(onClear || clearBlockedReason) && (
              <button
                type="button"
                className="ppv-icon-btn is-danger"
                onClick={() => onClear?.()}
                disabled={!onClear}
                aria-label={`Clear the values under ${row.category} on this project`}
                title={clearBlockedReason ?? `Clear the values under ${row.category} on this project and remove the row`}
              >
                <Trash2 size={15} aria-hidden="true" />
              </button>
            )}
          </div>
        </>
      )}
    </li>
  );
}

export function PnlProjectViewModal({
  isOpen,
  onClose,
  projectId,
  projectName,
  orgCategories,
  valueRows,
  view,
  loadError,
  saving,
  onSave,
  canClear,
  onValuesChanged,
}: PnlProjectViewModalProps) {
  const { addToast } = useToast();
  const { clearing, clearCategory, restoreBatch, clearedCategoryCount } = useClearProjectCategory();
  const [draft, setDraft] = useState<ProjectPnlView>(view);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [clearTarget, setClearTarget] = useState<string | null>(null);
  // Null until answered, and null again if it cannot be answered. Never 0 by
  // default: "no categories were cleared here" is a statement about money, and
  // this screen must not make it on the strength of a call that was refused or
  // a function that is not deployed.
  const [clearedCount, setClearedCount] = useState<number | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  // What the database currently holds, as far as this dialog knows. State
  // rather than the `view` prop, and it moves only when a write comes back
  // confirmed, so a refresh arriving mid-session can never make a saved draft
  // look unsaved, and an unconfirmed one can never make an unsaved draft look
  // saved.
  const [baseline, setBaseline] = useState<ProjectPnlView>(view);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const facts = useMemo(() => summariseByCategory(valueRows), [valueRows]);
  const valued = useMemo(() => valuedCategories(valueRows), [valueRows]);

  const resolved = useMemo(
    () => resolveProjectPnlRows({ orgCategories, view: draft, valued }),
    [orgCategories, draft, valued],
  );

  // The project's ordering of EVERY organisation category, hidden ones
  // included, which is what a drag rewrites and what the overlay stores.
  // Derived from the draft rather than held as a second piece of state, so
  // there is one source of truth for the order.
  const fullOrder = useMemo(
    () =>
      resolveProjectPnlRows({
        orgCategories,
        view: { ...draft, hidden: [] },
        valued: new Set<string>(),
      }).rows.map((row) => row.category),
    [orgCategories, draft],
  );

  const orderIndex = useMemo(() => new Map(fullOrder.map((name, index) => [name, index])), [fullOrder]);

  const visibleByGroup = useMemo(() => {
    const groups = new Map<string, ProjectPnlRow[]>();
    for (const group of GROUPS) groups.set(group.name, []);
    for (const row of resolved.rows) {
      const bucket = groups.get(row.group) ?? groups.get("Other");
      bucket?.push(row);
    }
    for (const bucket of groups.values()) {
      bucket.sort((a, b) => (orderIndex.get(a.category) ?? 0) - (orderIndex.get(b.category) ?? 0));
    }
    return groups;
  }, [resolved.rows, orderIndex]);

  /** Hidden and actually hidden: a hidden category holding values renders above with a chip instead. */
  const hiddenRows = useMemo(() => {
    const rendered = new Set(resolved.rows.map((row) => row.category));
    const known = new Set(orgCategories.map((category) => category.name));
    const groupOf = new Map(orgCategories.map((category) => [category.name, category.group]));
    return fullOrder
      .filter((name) => known.has(name) && draft.hidden.includes(name) && !rendered.has(name))
      .map((name) => ({ name, group: groupOf.get(name) ?? "Other" }));
  }, [resolved.rows, orgCategories, fullOrder, draft.hidden]);

  const emptyCandidates = resolved.emptyCandidates;
  const dirty = !sameView(draft, baseline);

  const handleDragEnd = useCallback(
    (groupName: string) => (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const groupNames = (visibleByGroup.get(groupName) ?? []).map((row) => row.category);
      const from = groupNames.indexOf(String(active.id));
      const to = groupNames.indexOf(String(over.id));
      if (from === -1 || to === -1) return;
      const nextGroupNames = arrayMove(groupNames, from, to);
      setDraft((current) => withOrder(current, reorderWithinFullOrder(fullOrder, groupNames, nextGroupNames)));
    },
    [visibleByGroup, fullOrder],
  );

  const hideEveryEmptyRow = useCallback(() => {
    setDraft((current) => emptyCandidates.reduce((next, name) => withHidden(next, name), current));
  }, [emptyCandidates]);

  const resetToOrganisationLayout = useCallback(() => {
    setDraft({ version: 1, hidden: [], order: [], labels: {} });
  }, []);

  const save = useCallback(async () => {
    setWriteError(null);
    const committed = draft;
    const result = await onSave(committed);
    if (!result.ok) {
      setWriteError(result.message ?? "Nothing was saved.");
      return;
    }
    setBaseline(committed);
    onClose();
  }, [draft, onSave, onClose]);

  // Fetched for everyone who can open this dialog, which already means
  // can_view_financials. The count is the half of the recovery surface that is
  // theirs: they are shown every figure on this P&L, so a project whose costs
  // fell would read as a more profitable project unless something says why.
  // The detail behind it, what was cleared and the way back, stays with the
  // admin pair below.
  useEffect(() => {
    if (!isOpen || !projectId) return;
    let cancelled = false;
    void (async () => {
      const outcome = await clearedCategoryCount(projectId);
      if (!cancelled) setClearedCount(outcome.count);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId, clearedCategoryCount]);

  const rowsForClear = useMemo(
    () => (clearTarget ? valueRows.filter((row) => row.category === clearTarget) : []),
    [clearTarget, valueRows],
  );

  /**
   * Puts a cleared batch straight back, from the Undo on the toast of the
   * clear itself.
   *
   * This is the shortest of the three routes back and the one that covers the
   * stated failure outright, because the person who clears the wrong project is
   * almost always still looking at it. It is not a timed window: the toast is
   * only the first place the batch appears, and the same restore is on the
   * panel below and in P&L Analytics for as long as the batch exists.
   */
  const undoClear = useCallback(
    async (batchId: string) => {
      const outcome = await restoreBatch(batchId);
      if (!outcome.ok) {
        addToast({ type: "error", title: "Nothing was restored", message: outcome.message, duration: 12000 });
        return;
      }
      addToast({
        type: "success",
        title: `${outcome.result.category} restored`,
        message: describeRestoreOutcome(outcome.result),
        duration: 12000,
      });
      onValuesChanged();
    },
    [restoreBatch, addToast, onValuesChanged],
  );

  const confirmClear = useCallback(
    async (expectedRowIds: string[]) => {
      const category = clearTarget ?? "";
      const outcome = await clearCategory({ projectId, category, expectedRowIds });
      if (!outcome.ok) {
        // A stale set is the caller's cue to refetch, which onValuesChanged
        // does. The dialog keeps itself open and shows the refusal, so the
        // reloaded figures are what the user reads before trying again.
        if (outcome.stale) onValuesChanged();
        return outcome;
      }

      const { result } = outcome;
      setClearTarget(null);
      // The RPC hid the category itself, in the same transaction. Moving the
      // local draft and its baseline together keeps the editor clean, so the
      // next Save cannot write the category back out of the hidden list.
      setDraft((current) => withHidden(current, result.category));
      setBaseline((current) => withHidden(current, result.category));
      addToast({
        type: "success",
        title: `${formatCurrency(result.archivedTotal)} cleared from ${result.category}`,
        message: describeClearOutcome(result),
        duration: 20000,
        action: { label: "Undo, put it back", onClick: () => void undoClear(result.batchId) },
      });
      onValuesChanged();
      return outcome;
    },
    [clearTarget, clearCategory, projectId, addToast, onValuesChanged, undoClear],
  );

  if (!isOpen) return null;

  const shownCount = resolved.rows.length;
  const totalCount = orgCategories.length;

  /**
   * Why a row offers no Clear, in the words the person needs, or null when the
   * question does not arise because the row holds nothing to clear.
   *
   * A permission refusal states BOTH halves of the rule. An admin told only
   * that "admins can do this" would go looking for a role they already hold.
   */
  function clearBlockedReasonFor(category: string): string | null {
    if (!facts.has(category)) return null;
    if (!canClear) {
      return "Clearing typed values needs an organisation admin role and financial access, together.";
    }
    if (dirty) {
      return "Save or cancel the layout changes first. Clearing writes this project's layout itself, so the two cannot be in flight at once.";
    }
    return null;
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      hideFooter
      ariaLabel={`P and L rows on ${projectName}`}
      title={
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text-primary)" }}>P&amp;L rows on this project</div>
          <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 2 }}>{projectName}</div>
        </div>
      }
    >
      <p className="ppv-intro">
        Expense categories are shared by every project, so a line that does not apply here is still printed here.
        Turning one off changes <strong>this project's</strong> P&amp;L and nothing else. No figure moves anywhere.
      </p>

      {loadError && (
        <div className="ppv-notice is-warning">
          <AlertTriangle size={15} aria-hidden="true" />
          <span>
            This project's saved layout could not be read, so every category is showing. Saving now would replace
            whatever is stored. {loadError}
          </span>
        </div>
      )}

      {writeError && (
        <div className="ppv-notice is-error" role="alert">
          <AlertTriangle size={15} aria-hidden="true" />
          <span>{writeError}</span>
        </div>
      )}

      <div className="ppv-summary">
        <span className="ppv-summary-count">
          <span className="ppv-num" key={shownCount}>
            {shownCount}
          </span>{" "}
          of {totalCount} rows shown
        </span>
        <span className="ppv-summary-note">
          {emptyCandidates.length > 0
            ? `${emptyCandidates.length} of them have never held a value on this project.`
            : "Every row showing here holds at least one value."}
        </span>
      </div>

      {emptyCandidates.length > 0 && (
        <button type="button" className="ppv-bulk" onClick={hideEveryEmptyRow}>
          <EyeOff size={15} aria-hidden="true" />
          Hide the {emptyCandidates.length} {emptyCandidates.length === 1 ? "row" : "rows"} with no values on this
          project
          <span className="ppv-bulk-sub">
            Each one reads zero in every month and in the total, so nothing on the P&amp;L changes except the number of
            rows you scroll past. You can bring any of them back below.
          </span>
        </button>
      )}

      {GROUPS.map((group) => {
        const rows = visibleByGroup.get(group.name) ?? [];
        const inGroup = orgCategories.filter((category) => category.group === group.name).length;
        if (inGroup === 0) return null;
        const GroupIcon = group.icon;

        return (
          <section key={group.name}>
            <h4 className="ppv-group-label">
              <GroupIcon size={12} aria-hidden="true" />
              {group.label}
              <span className="ppv-group-meta">
                {rows.length} of {inGroup} shown
              </span>
            </h4>
            {rows.length === 0 ? (
              <p className="ppv-archive-empty">Every {group.label.toLowerCase()} row is hidden on this project.</p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd(group.name)}
              >
                <SortableContext
                  items={rows.map((row) => row.category)}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="ppv-list" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {rows.map((row) => (
                      <SortableRow
                        key={row.category}
                        row={row}
                        facts={facts.get(row.category)}
                        canHide={emptyCandidates.includes(row.category)}
                        clearBlockedReason={clearBlockedReasonFor(row.category)}
                        onClear={
                          canClear && !dirty && facts.has(row.category)
                            ? () => setClearTarget(row.category)
                            : null
                        }
                        editing={editingCategory === row.category}
                        onStartEditing={() => setEditingCategory(row.category)}
                        onStopEditing={() => setEditingCategory(null)}
                        onLabel={(label) => setDraft((current) => withLabel(current, row.category, label))}
                        onHide={() => setDraft((current) => withHidden(current, row.category))}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </section>
        );
      })}

      {hiddenRows.length > 0 && (
        <div className="ppv-hidden-block">
          <h4 className="ppv-group-label">
            <EyeOff size={12} aria-hidden="true" />
            Hidden on this project
            <span className="ppv-group-meta">still counted everywhere, just not printed here</span>
          </h4>
          <ul className="ppv-hidden-grid" style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {hiddenRows.map((hiddenRow) => (
              <li className="ppv-row is-hidden" key={hiddenRow.name}>
                <span className="ppv-handle" aria-hidden="true">
                  <EyeOff size={15} />
                </span>
                <div className="ppv-names">
                  <span className="ppv-name">{draft.labels[hiddenRow.name] ?? hiddenRow.name}</span>
                  <span className="ppv-money is-empty">
                    {hiddenRow.group === "Other" ? "Other costs" : hiddenRow.group}, no values on this project
                  </span>
                </div>
                <div className="ppv-actions">
                  <button
                    type="button"
                    className="ppv-icon-btn"
                    onClick={() => setDraft((current) => withHidden(current, hiddenRow.name, false))}
                    aria-label={`Show ${hiddenRow.name} on this project again`}
                    title={`Show ${hiddenRow.name} on this project again`}
                  >
                    <Eye size={15} aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recovery route 2: on the project, where the mistake is noticed. The
          same panel that sits in P&L Analytics, filtered to this project, so
          there is one component and one set of rules rather than a per project
          copy that drifts. Its collapsed header IS the "N cleared on this
          project" line, with the count already fetched.

          Shown only to someone who can read the archive. The spec offers the
          bare count more widely, but the table's SELECT policy is admin AND
          financial access, so a count for anyone else would have to be invented
          on the client, and an invented count on a screen about missing money
          is worse than no line at all. Reported to the board. */}
      {canClear ? (
        <div style={{ marginTop: 20 }}>
          <PnlArchivedValues
            projectNames={new Map([[projectId, projectName]])}
            filterProjectId={projectId}
            onRestored={onValuesChanged}
          />
        </div>
      ) : (
        clearedCount !== null && clearedCount > 0 && (
          <p className="ppv-cleared-line">
            <AlertTriangle size={15} aria-hidden="true" />
            <span>
              <strong>
                {clearedCount} {clearedCount === 1 ? "category holds" : "categories hold"} cleared values on this
                project.
              </strong>{" "}
              Money that was typed here has been removed, so this project's costs are lower than they were.
            </span>
            <span className="ppv-cleared-meta">
              An organisation admin with financial access can see what was cleared, by whom and when, and put it back.
            </span>
          </p>
        )
      )}

      <div className="ppv-footer">
        <button
          type="button"
          className="ppv-link"
          onClick={resetToOrganisationLayout}
          title="Show every organisation category again, in the organisation's own order, with no renames"
        >
          <RotateCcw size={12} aria-hidden="true" style={{ marginRight: 4, verticalAlign: -1 }} />
          Reset layout
        </button>
        {/* Only when there is something to say. A standing "matches what is
            saved" line costs a row of a dialog that is already tall at 390px,
            and the Save button being off says the same thing. */}
        {dirty && (
          <span className="ppv-footer-status is-dirty">
            Not written yet. Saving applies this layout for everyone.
          </span>
        )}
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={save}
          disabled={!dirty || saving}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          {saving && <Loader2 size={14} className="spin" aria-hidden="true" />}
          {saving ? "Saving" : "Save layout"}
        </button>
      </div>

      {/* Stacked on this one. The shared Modal keeps an open dialog stack, so
          Escape and the focus trap belong to whichever is on top, and closing
          the confirm returns the user to the editor rather than to the tab. */}
      {clearTarget && (
        <PnlClearCategoryDialog
          isOpen
          onClose={() => setClearTarget(null)}
          projectName={projectName}
          category={clearTarget}
          projectLabel={draft.labels[clearTarget] ?? clearTarget}
          rows={rowsForClear}
          clearing={clearing}
          onConfirm={confirmClear}
          onStale={onValuesChanged}
        />
      )}
    </Modal>
  );
}
